import type { TrendingItem } from '@/types';
import { useAppDispatch } from '@/app/hooks';
import { showToast, setTranscriptSearch } from '@/features/ui/uiSlice';
import { fetchStorageAsset } from '@/features/search/searchSlice';
import { setPlaying, updateCurrentTime, requestAutoplay } from '@/features/player/playerSlice';
import { useHoverVideoPreview } from '@/hooks/useHoverVideoPreview';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import demoVideo from '@/assets/videos/Image_from_iOS.MOV';
import styles from './TrendingTile.module.css';

interface TrendingTileProps {
  item: TrendingItem;
  variant?: 'grid';
  /** Permanently delete the underlying asset. Renders a delete button on hover. */
  onDelete?: () => void;
}

export default function TrendingTile({ item, variant, onDelete }: TrendingTileProps) {
  const dispatch = useAppDispatch();

  // Use real storage_url when available; hook handles lazy loading + 5 s loop
  const { videoRef, handleMouseEnter, handleMouseLeave } = useHoverVideoPreview(
    item.storage_url
  );

  const handleSendToStoryHub = () => {
    trackEvent(ANALYTICS_EVENTS.TREND_ADAPT, { assetId: item.id, source: 'trending_tile' });
    dispatch(
      showToast({
        title: 'Sent to Story Hub',
        message: `Creative brief created for <b style="color:var(--color-text)">${item.title}</b><br/>Format: ${item.platformLabel} vertical · Hook pattern pre-populated`,
      })
    );
  };

  // Clicking the tile thumbnail plays the video in the hero player when storage_url exists
  const handleTileClick = () => {
    if (!item.storage_url) return;
    trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_PLAY, { assetId: item.id, tileType: 'trending' });
    dispatch(setPlaying(false));
    dispatch(updateCurrentTime(0));
    dispatch(requestAutoplay());
    dispatch(setTranscriptSearch(''));
    dispatch(fetchStorageAsset(item.id));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={styles.tile} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        className={`${styles.thumb} tile-bg-${item.gradient}`}
        onClick={handleTileClick}
        role={item.storage_url ? 'button' : undefined}
        tabIndex={item.storage_url ? 0 : undefined}
        aria-label={item.storage_url ? `Play ${item.title}` : undefined}
        onKeyDown={item.storage_url ? (e) => e.key === 'Enter' && handleTileClick() : undefined}
        style={item.storage_url ? { cursor: 'pointer' } : undefined}
      >
        <video
          ref={videoRef}
          className={styles.thumbVideo}
          src={item.storage_url ? undefined : demoVideo}
          muted
          loop={!item.storage_url}
          playsInline
          preload="none"
        />
        <span className={styles.matchBadge}>⚡ MATCH</span>
        <span className={styles.platBadge}>
          <span className={`${styles.platIcon} plat-${item.platform}`} />
          {item.platformLabel}
        </span>
        <span className={styles.duration}>{item.duration}</span>
        <span className={styles.clipRange}>{item.clipRange}</span>
        {onDelete && (
          <button
            className={styles.deleteBtn}
            title="Delete video"
            aria-label="Delete video"
            onClick={(e) => { e.stopPropagation(); trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_DELETE, { assetId: item.id, tileType: 'trending' }); onDelete(); }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        )}
        {variant === 'grid' && (
          <div className={styles.thumbTitleOverlay}>
            <span className={styles.thumbTitleText}>{item.title}</span>
          </div>
        )}
      </div>

      {variant !== 'grid' && <div className={styles.body}>
        <p className={styles.hook}>{item.title}</p>
        <div className={styles.matchRow}>
          <span className={styles.label}>Matches</span>
          {item.matchPattern}
          <b className={styles.delta}>{item.trendDelta}</b>
        </div>
        <div className={styles.reachRow}>
          <span className={styles.label}>Projected reach</span>
          <b className={styles.reach}>{item.projectedReach}</b> on {item.platformLabel}
        </div>
        <div className={styles.actions}>
          <button className={styles.btnPrimary} onClick={handleSendToStoryHub}>
            → Send to Story Hub
          </button>
          <button className={styles.btnGhost}>Preview clip</button>
        </div>
      </div>}
    </div>
  );
}
